import React, { useState } from "react";
import Amplify, { Auth } from "aws-amplify";
import {
    CognitoUserInterface,
    AuthState,
    onAuthUIStateChange
} from "@aws-amplify/ui-components";
import awsconfig from "./aws-exports";
import AppLayout from "./pages/AppLayout";
import "./App.css";
import { setUser } from "./store/actions/Auth";
import { useDispatch, useSelector } from "react-redux";
import HoldingPage from "./components/HoldingPage";
import ReactGA from "react-ga4";
import GoogleAnalyticsConfig from "./config/GoogleAnalyticsConfig";

Amplify.configure(awsconfig);

if (GoogleAnalyticsConfig.trackingId !== "") {
    ReactGA.initialize(GoogleAnalyticsConfig.trackingId, {
        gaOptions: {
            debug_mode: true
        }
    });

    ReactGA.set({
        pageUrl: `{window.location.pathname + window.location.search}`
    });
    ReactGA.send("pageview");
} else {
    console.debug("Google analytics not configured");
}

export default function App() {
    const user = useSelector((state: any) => state.portal.user);
    const dispatch = useDispatch();
    const [authState, setAuthState] = useState<AuthState>();
    const AUTH_STATE_LOCAL_STATE_KEY = "local-authstate";

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    React.useEffect(() => {
        // Cleanup function to unsubscribe on unmount
        const unsubscribe = onAuthUIStateChange((nextAuthState, authData) => {
            setAuthState(nextAuthState);

            // Dispatch user to Redux if auth state is signed in
            if (nextAuthState === "signedin" && authData) {
                const newUser = authData as CognitoUserInterface;
                dispatch(setUser(newUser));
                localStorage.setItem(AUTH_STATE_LOCAL_STATE_KEY, nextAuthState);
            }
        });

        const updateUserFromAmplifyAuth = async () => {
            try {
                const newUser = await Auth.currentAuthenticatedUser();

                // Ensure user and authState are synchronized
                if (newUser && (!authState || authState === "signedin")) {
                    dispatch(setUser(newUser));
                }
            } catch (err) {
                console.error("Error fetching authenticated user:", err);
                dispatch(setUser(undefined)); // Clear user if fetching fails
            }
        };

        // Run user restoration logic on mount or when authState updates
        updateUserFromAmplifyAuth().catch((result) => {
            console.debug("Error during user restoration:", result);
        });

        return () => unsubscribe(); // Cleanup the `onAuthUIStateChange` listener
    }, [authState, dispatch]);

    if (token !== null) {
        return (
            <div className="App">
                <HoldingPage token={token} setAuthState={setAuthState} />
            </div>
        );
    }

    return user ? (
        <div className="App">
            <AppLayout user={user} />
        </div>
    ) : (
        <div className="App">
            <HoldingPage token={token} setAuthState={setAuthState} />
        </div>
    );
}
